import i18next from 'i18next';
import { toast } from 'sonner';
import { useDataBreachStore } from '@/store/data-breach.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class DataBreachIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchItems(params = {}) {
        const store = useDataBreachStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        if (store.clearError) store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const page = params.page || store.paginationData?.currentPage || 1;
            const pageSize = params.pageSize || store.paginationData?.itemsPerPage || 10;
            const search = params.search ?? '';
            const sortBy = params.sortBy || 'createdAt';
            const sortOrder = params.sortOrder || 'desc';
            const columnFilters = params.columnFilters ?? store.columnFilters ?? {};

            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value?.length) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });

            store.setItems(response.data || []);
            if (response.pagination) {
                store.setPaginationData({
                    currentPage: response.pagination.page,
                    totalPages: response.pagination.totalPages,
                    totalItems: response.pagination.total,
                    itemsPerPage: response.pagination.pageSize,
                });
            }
            if (response.uniqueValues) store.setUniqueValues(response.uniqueValues);
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async createItem(data) {
        const store = useDataBreachStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(
                i18next.t('gdprDataBreach:messages.createSuccess', {
                    defaultValue: 'Breach record created',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateItem(id, data) {
        const store = useDataBreachStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(
                i18next.t('gdprDataBreach:messages.updateSuccess', {
                    defaultValue: 'Breach record updated',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteItem(id) {
        const store = useDataBreachStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.delete(id);
            toast.success(
                i18next.t('gdprDataBreach:messages.deleteSuccess', {
                    defaultValue: 'Breach record deleted',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async batchDeleteItems(ids) {
        try {
            const response = await this.service.batchDelete(ids);
            toast.success(
                i18next.t('gdprDataBreach:messages.bulkDeleteSuccess', { defaultValue: 'Selected records deleted' }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async openAuthorityForm(id) {
        try {
            const result = await this.service.getAuthorityForm(id);
            const html = result.data;
            const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            toast.success(
                i18next.t('gdprDataBreach:messages.authorityFormOpened', {
                    defaultValue: 'Authority notification form opened',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
